import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import Pagination from "./components/Pagination";
import {
  DataSheetGrid,
  textColumn,
  keyColumn,
  checkboxColumn,
} from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";

// Validation helpers
const validateSampleSheet = async (sampleSheet, pipelineId, selectedFileIds = []) => {
  if (!Array.isArray(sampleSheet) || sampleSheet.length === 0) {
    return { valid: false, message: "Sample sheet is empty or invalid." };
  }

  const headers = Object.keys(sampleSheet[0] || {});
  if (headers.length === 0) {
    return { valid: false, message: "Sample sheet has no columns." };
  }

  let requiredCols = [];
  try {
    const res = await axios.get(`${API_BASE}/nfcore-pipelines/inputs/${pipelineId}`);
    const rows = res.data?.inputs || res.data || [];
    requiredCols = rows
      .filter(r => String(r.user_input).toUpperCase() === "Y" && String(r.required).toUpperCase() === "Y")
      .map(r => r.column_name);
  } catch (e) {
    return { valid: false, message: "Unable to fetch pipeline input metadata." };
  }

  if (requiredCols.length === 0) {
    return { valid: false, message: "No required inputs defined for this pipeline." };
  }

  const requiredFileRefs = [];
  sampleSheet.forEach((row, rowIndex) => {
    requiredCols.forEach((col) => {
      const val = row[col];
      if (val && typeof val === "string" && val.trim() !== "") {
        requiredFileRefs.push({ row: rowIndex + 1, column: col, value: val.trim() });
      }
    });
  });

  if (requiredFileRefs.length === 0) {
    return { valid: false, message: "No required file references found in sample sheet." };
  }

  const selectedFileNames = [];
  for (const id of selectedFileIds) {
    try {
      const res = await axios.get(`${API_BASE}/user-files/${id}`);
      const filename = res.data?.file?.filename?.trim();
      if (filename) selectedFileNames.push(filename);
    } catch (e) {
      console.warn(`⚠️ Failed to fetch file metadata for ID ${id}`);
    }
  }

  if (requiredFileRefs.length > selectedFileNames.length) {
    return {
      valid: false,
      message: `Sample sheet references ${requiredFileRefs.length} files, but only ${selectedFileNames.length} were selected.`,
    };
  }

  const unmatchedRefs = requiredFileRefs.filter(
    (ref) => !selectedFileNames.includes(ref.value)
  );

  if (unmatchedRefs.length > 0) {
    return {
      valid: false,
      message: `Missing ${unmatchedRefs.length} required files. Please ensure all referenced files are selected.`,
    };
  }

  return { valid: true };
};

const fetchRequiredInputCount = async (pipelineId) => {
  try {
    const res = await axios.get(`${API_BASE}/nfcore-pipelines/inputs/${pipelineId}`);
    const rows = res.data || [];
    const requiredCols = rows.filter(
      (r) =>
        String(r.user_input).toUpperCase() === "Y" &&
        String(r.required).toUpperCase() === "Y"
    );
    return requiredCols.length;
  } catch (e) {
    console.error("Failed to fetch required input definitions:", e);
    return null;
  }
};

const PipelineLaunch = () => {
  const { pipelineId: paramPipelineId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { pipelineId = paramPipelineId, pipelineName, sampleSheet, selectedParams } = state || {};
  const [summary, setSummary] = useState("");
  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [cognitoUserId, setCognitoUserId] = useState("");
  const [organization, setOrganization] = useState("");
  const [showLaunchPopup, setShowLaunchPopup] = useState(false);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const [mandatoryOptions, setMandatoryOptions] = useState([]);
  const [paramFileSelections, setParamFileSelections] = useState({});
  const [paramSearchStates, setParamSearchStates] = useState({});
  const [paramPageStates, setParamPageStates] = useState({});
  const [launchMessages, setLaunchMessages] = useState([]);
  const [isLaunching, setIsLaunching] = useState(false);



  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axios.get(`${API_BASE}/nfcore-pipelines/options/${pipelineId}`);
        setPipelineOptions(res.data);
      } catch (err) {
        console.error("Failed to fetch pipeline options:", err);
      }
    };
    fetchOptions();
  }, [pipelineId]);

  useEffect(() => {
    const fetchMandatoryOptions = async () => {
      try {
        const res = await axios.get(`${API_BASE}/nfcore-pipelines/mandatory_options/${pipelineId}`);
        setMandatoryOptions(res.data);
      } catch (err) {
        console.error("Failed to fetch mandatory pipeline options:", err);
      }
    };
    fetchMandatoryOptions();
  }, [pipelineId]);

  /* const activeFileParams = useMemo(() => {
    if (!pipelineOptions || !selectedParams) return [];

    return pipelineOptions.filter(
      (opt) =>
        opt.user_input === "Y" &&
        opt.type !== "radio" &&
        selectedParams[opt.parameter] === "true"
    );
  }, [pipelineOptions, selectedParams]); */
  const activeFileParams = pipelineOptions.filter(
    (opt) =>
      opt.user_input === "Y" &&
      opt.required === "Y" &&
      opt.type === "file" &&
      !opt.hidden
  );

  console.log("Selected Params:", selectedParams);
  console.log("Pipeline Options:", pipelineOptions);
  console.log("Active File Params:", activeFileParams);
  /* useEffect(() => {
    console.log("Selected Params:", selectedParams);
    console.log("Pipeline Options:", pipelineOptions);
    console.log("Active File Params:", activeFileParams);
  }, [selectedParams, pipelineOptions, activeFileParams]); */
  
  const filteredFiles = searchQuery.trim()
    ? existingFiles.filter(
        (file) =>
          file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          file.project.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : existingFiles;

  const filesPerPage = 10;
  const paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * filesPerPage,
    currentPage * filesPerPage
  );
  const totalPages = Math.ceil(filteredFiles.length / filesPerPage);

  const rowsWithSelect = useMemo(
    () =>
      paginatedFiles.map((f) => ({
        ...f,
        select: selectedFiles.includes(f.id),
      })),
    [paginatedFiles, selectedFiles]
  );

  const columns = [
    {
      ...keyColumn("select", checkboxColumn),
      title: (
        <input
          type="checkbox"
          ref={(el) => {
            if (el) {
              const allSelected = paginatedFiles.every((file) =>
                selectedFiles.includes(file.id)
              );
              const noneSelected = paginatedFiles.every((file) =>
                !selectedFiles.includes(file.id)
              );
              el.indeterminate = !allSelected && !noneSelected;
            }
          }}
          checked={
            paginatedFiles.length > 0 &&
            paginatedFiles.every((file) => selectedFiles.includes(file.id))
          }
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked) {
              const newSelections = paginatedFiles
                .map((file) => file.id)
                .filter((id) => !selectedFiles.includes(id));
              setSelectedFiles((prev) => [...prev, ...newSelections]);
            } else {
              setSelectedFiles((prev) =>
                prev.filter((id) => !paginatedFiles.some((file) => file.id === id))
              );
            }
          }}
        />
      ),
      width: 40,
    },
    { ...keyColumn("name", textColumn), title: "Filename", disabled: true, width: 300 },
    { ...keyColumn("project", textColumn), title: "Project", disabled: true, width: 120 },
    { ...keyColumn("path", textColumn), title: "S3 Path", disabled: true, width: 400 },
    { ...keyColumn("created", textColumn), title: "Created On", disabled: true, width: 140 },
  ];
  useEffect(() => {
    const fetchExistingFiles = async () => {
      try {
        const idToken = localStorage.getItem("idToken");
        const response = await fetch(`${API_BASE}/user-files`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ cognitoUserId }),
        });

        const data = await response.json();
        const normalizedFiles = data.files.map((file) => ({
          id: file.id,
          name: file.filename,
          project: file.original_project,
          path: file.s3_folder_path,
          created: new Date(file.created_on).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        }));

        setExistingFiles(normalizedFiles);
      } catch (err) {
        console.error("Failed to fetch existing files:", err);
      }
    };

    if (cognitoUserId) {
      fetchExistingFiles();
    }
  }, [cognitoUserId]);

  useEffect(() => {
    const fetchCognitoUserId = async () => {
      try {
        const idToken = localStorage.getItem("idToken");
        if (!idToken) return;

        const response = await fetch(`${API_BASE}/get-user-id`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        });
        const data = await response.json();
        if (data.cognitoUserId) setCognitoUserId(data.cognitoUserId);
      } catch (error) {
        console.error("Error fetching Cognito User ID:", error);
      }
    };

    fetchCognitoUserId();
  }, []);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const idToken = localStorage.getItem("idToken");
        if (!idToken) return;

        const response = await fetch(`${API_BASE}/get-user-id`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        });

        const data = await response.json();
        if (data.organization) setOrganization(data.organization);
      } catch (error) {
        console.error("Error fetching Cognito user Organization:", error);
      }
    };

    fetchOrganization();
  }, []);
  
  useEffect(() => {
    const fetchPipelineDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE}/nfcore-pipelines/${pipelineId}`);
        setSummary(res.data.summary || "No summary available.");
      } catch (err) {
        setSummary("Error fetching summary.");
      }
    };
    fetchPipelineDetails();
  }, [pipelineId]);

  const handleLaunchConfirm = () => {
    setIsLaunching(false);
    setShowLaunchPopup(true);
  };

  const handleLaunch = async () => {
    try {
      const validation = await validateSampleSheet(sampleSheet, pipelineId, selectedFiles);
      if (!validation.valid) {
        alert(validation.message || "Sample sheet validation failed.");
        return;
      }

      const requiredCount = await fetchRequiredInputCount(pipelineId);
      if (requiredCount === null) {
        alert("Unable to verify required inputs right now.");
        return;
      }

      if (selectedFiles.length < requiredCount) {
        alert(`Insufficient files selected. Required: ${requiredCount}, Provided: ${selectedFiles.length}.`);
        return;
      }

      setIsLaunching(true);
      setLaunchMessages(["Starting launch..."]);

      // ✅ Step 1: POST launch payload
      const postRes = await fetch(`${API_BASE}/nfcore/launch-analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({
          pipelineId,
          pipelineName,
          sampleSheet,
          selectedParams,
          paramFileSelections,
          selectedFileIds: selectedFiles,
          cognitoUserId, 
          organization,
          mandatoryOptions
        }),
      });
      
      if (!postRes.ok) {
        throw new Error("Failed to initiate launch.");
      }

      // ✅ Step 2: Open SSE stream after short delay
      setTimeout(() => {
        const eventSource = new EventSource(`${API_BASE}/nfcore/launch-analysis/stream`);

        eventSource.onmessage = (e) => {
          setLaunchMessages((prev) => [...prev, e.data]);

          if (e.data.startsWith("✅")) {
            eventSource.close();
            setTimeout(() => {
              setShowLaunchPopup(false);
              setIsLaunching(false);
              navigate("/analyses");
            }, 2000);
          }

          if (e.data.startsWith("❌")) {
            eventSource.close();
            setIsLaunching(false);
          }
        };

        eventSource.onerror = (err) => {
          console.error("SSE error:", err);
          setLaunchMessages((prev) => [...prev, "❌ Connection error"]);
          eventSource.close();
          setIsLaunching(false);
        };
      }, 500); // ⏳ Give backend time to store payload
      console.log("SELECTED PARAMS: ", selectedParams);

    } catch (err) {
      console.error("Pipeline launch validation failed:", err);
      setLaunchMessages(["❌ Launch failed. Please try again."]);
      setIsLaunching(false);
      setShowLaunchPopup(false);
    }
  };
  
  return (
    <div style={{ padding: "30px" }}>
      <h1>LAUNCH PIPELINE {pipelineName?.toUpperCase()} ON YOUR DATA</h1>

      <section style={{ marginBottom: "20px" }}>
        <h2>Summary</h2>
        <p>{summary}</p>
      </section>

      <section style={{ marginTop: "20px" }}>
        <h2>Select Data Files</h2>
        <h4>Select the files you'd like to use in your analysis. If you need to use new data, first upload files by going to "Your Data" on top.</h4>
        <input
          type="text"
          placeholder="Search by filename or project"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: "8px",
            fontSize: "14px",
            width: "300px",
            marginBottom: "16px",
          }}
        />
        <div
          style={{
            height: `${36 * 11 + 40}px`,
            overflowY: "auto",
            border: "1px solid #ccc",
            padding: "1rem",
          }}
        >
          <DataSheetGrid
            columns={columns}
            value={rowsWithSelect}
            keyColumn="id"
            disableAddRows={true}
            disableEdit={true}
            rowHeight={36}
            onChange={(updatedRows) => {
              const updatedIds = updatedRows
                .filter((row) => row.select)
                .map((row) => row.id);

              const deselectedIds = updatedRows
                .filter((row) => !row.select)
                .map((row) => row.id);

              setSelectedFiles((prev) => {
                const withoutDeselected = prev.filter((id) => !deselectedIds.includes(id));
                const combined = [...withoutDeselected, ...updatedIds];
                return Array.from(new Set(combined));
              });
            }}
          />
        </div>

        <Pagination page={currentPage} totalPages={totalPages} onChange={setCurrentPage} />

        {filteredFiles.length === 0 && (
          <p style={{ marginTop: "12px", color: "#888" }}>No matching files found.</p>
        )}
      </section>

      {/* ✅ Additional file selectors for dependent params */}
      {activeFileParams.map((param) => {
        const localSearch = paramSearchStates[param.parameter] || "";
        const localPage = paramPageStates[param.parameter] || 1;

        const filtered = localSearch.trim()
          ? existingFiles.filter(
              (file) =>
                file.name.toLowerCase().includes(localSearch.toLowerCase()) ||
                file.project.toLowerCase().includes(localSearch.toLowerCase())
            )
          : existingFiles;

        const filesPerPage = 10;
        const paginated = filtered.slice(
          (localPage - 1) * filesPerPage,
          localPage * filesPerPage
        );
        const totalPages = Math.ceil(filtered.length / filesPerPage);

        return (
          <section key={param.parameter} style={{ marginTop: "40px" }}>
            {/* <h3>Select file for: {param.parameter}</h3> */}
            <h3>{param.help}</h3>
            <input
              type="text"
              placeholder="Search by filename or project"
              value={localSearch}
              onChange={(e) => {
                const val = e.target.value;
                setParamSearchStates((prev) => ({
                  ...prev,
                  [param.parameter]: val,
                }));
                setParamPageStates((prev) => ({
                  ...prev,
                  [param.parameter]: 1,
                }));
              }}
              style={{
                padding: "8px",
                fontSize: "14px",
                width: "300px",
                marginBottom: "16px",
              }}
            />
            <DataSheetGrid
              columns={[
                {
                  ...keyColumn("select", checkboxColumn),
                  title: "Select",
                  width: 40,
                },
                { ...keyColumn("name", textColumn), title: "Filename", disabled: true, width: 300 },
                { ...keyColumn("project", textColumn), title: "Project", disabled: true, width: 120 },
                { ...keyColumn("path", textColumn), title: "S3 Path", disabled: true, width: 400 },
                { ...keyColumn("created", textColumn), title: "Created On", disabled: true, width: 140 },
              ]}
              value={paginated.map((file) => ({
                ...file,
                select: paramFileSelections[param.parameter] === file.id,
              }))}
              keyColumn="id"
              disableAddRows={true}
              disableEdit={true}
              rowHeight={36}
              onChange={(updatedRows) => {
                const selected = updatedRows.find((row) => row.select);
                setParamFileSelections((prev) => ({
                  ...prev,
                  [param.parameter]: selected?.id || null,
                }));
              }}
            />
            <Pagination
              page={localPage}
              totalPages={totalPages}
              onChange={(p) => setParamPageStates((prev) => ({ ...prev, [param.parameter]: p }))}
            />
          </section>
        );
      })}

      <section style={{ marginBottom: "20px" }}>
        <h2>SAMPLE SHEET INPUT</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {Object.keys(sampleSheet?.[0] || {}).map((col) => (
                <th key={col} style={{ border: "1px solid #ccc", padding: "8px" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sampleSheet?.map((row, idx) => (
              <tr key={idx}>
                {Object.values(row).map((val, i) => (
                  <td key={i} style={{ border: "1px solid #eee", padding: "8px" }}>
                    {val}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: "20px" }}>
        <h2>USER SELECTED PARAMETERS</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Parameter</th>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(selectedParams || {}).map(([key, val]) => (
              <tr key={key}>
                <td style={{ border: "1px solid #eee", padding: "8px" }}>{key}</td>
                <td style={{ border: "1px solid #eee", padding: "8px" }}>
                  {Array.isArray(val) ? val.join(", ") : val}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div style={{ textAlign: "center", marginTop: "30px" }}>
  <button
    onClick={handleLaunchConfirm}
    disabled={selectedFiles.length === 0}
    style={{
      padding: "12px 24px",
      fontSize: "16px",
      backgroundColor: selectedFiles.length > 0 ? "#28a745" : "#aaa",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: selectedFiles.length > 0 ? "pointer" : "not-allowed",
    }}
  >
    LAUNCH PIPELINE
  </button>
  </div>

  {showLaunchPopup && (
    <div
      style={{
        position: "fixed",
        top: "30%",
        left: "50%",
        transform: "translateX(-50%)",
        background: "#fff",
        border: "2px solid #444",
        padding: "30px",
        zIndex: 1000,
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        width: "400px",
        textAlign: "center",
      }}
    >
      {!isLaunching ? (
        <>
          <h3>DO YOU WANT TO LAUNCH THIS ANALYSIS WITH THE DATA?</h3>
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={handleLaunch}
              style={{
                padding: "10px 20px",
                marginRight: "10px",
                backgroundColor: "#28a745",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Yes, Launch
            </button>
            <button
              onClick={() => setShowLaunchPopup(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#dc3545",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>Launching Analysis</h3>
          <div style={{ marginTop: "20px" }}>
            <div className="spinner" style={{
              margin: "0 auto 20px",
              width: "40px",
              height: "40px",
              border: "4px solid #ccc",
              borderTop: "4px solid #007bff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }} />
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              {launchMessages.map((msg, idx) => (
                <li key={idx} style={{ marginBottom: "6px" }}>{msg}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )}
    </div>
  );
};

export default PipelineLaunch;
